import type { ScriptableContext } from 'chart.js';

/** Colores de la gráfica radar según el tema activo. */
interface RadarTheme {
  gridColor: string;
  labelColor: string;
  pointBorder: string;
}

const DARK_THEME: RadarTheme = {
  gridColor: 'rgba(255,255,255,0.16)',
  labelColor: '#94a3b8',
  pointBorder: 'rgba(30,18,68,0.9)',
};

const LIGHT_THEME: RadarTheme = {
  gridColor: 'rgba(0,0,0,0.10)',
  labelColor: '#9CA3AF',
  pointBorder: '#fff',
};

/** Escala de la encuesta: 1 = Inicio … 4 = Referente. */
const MAX_SCORE = 4;
/** Patrón de línea punteada para el grid de fondo y la serie histórica. */
const DASH_PATTERN = [4, 4];

export interface RadarSeriesInput {
  labels: string[];
  currentData: number[];
  pastData: number[];
  currentLabel: string;
  pastLabel: string;
  isDark: boolean;
}

/** Degradado radial: opaco en el centro, transparente hacia el borde. */
function radialFill(centerColor: string, edgeColor: string) {
  return (context: ScriptableContext<'radar'>) => {
    const { chart } = context;
    const { ctx, chartArea } = chart;
    if (!chartArea) return centerColor;

    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;
    const radius = (chartArea.right - chartArea.left) / 2;
    const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
    gradient.addColorStop(0, centerColor);
    gradient.addColorStop(1, edgeColor);
    return gradient;
  };
}

export function buildRadarData({
  labels,
  currentData,
  pastData,
  currentLabel,
  pastLabel,
  isDark,
}: RadarSeriesInput) {
  const { pointBorder } = isDark ? DARK_THEME : LIGHT_THEME;
  return {
    labels,
    datasets: [
      {
        label: currentLabel,
        data: currentData,
        borderColor: '#F472B6',
        backgroundColor: radialFill('rgba(244,114,182,0.65)', 'rgba(244,114,182,0.15)'),
        fill: true,
        pointBackgroundColor: '#F472B6',
        pointBorderColor: pointBorder,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointHoverBackgroundColor: '#FBCFE8',
        borderWidth: 2.5,
      },
      {
        label: pastLabel,
        data: pastData,
        borderColor: '#38BDF8',
        backgroundColor: 'transparent',
        fill: false,
        borderDash: DASH_PATTERN,
        pointBackgroundColor: '#38BDF8',
        pointBorderColor: pointBorder,
        pointBorderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 6,
        borderWidth: 1.5,
      },
    ],
  };
}

export function buildRadarOptions(isDark: boolean) {
  const { gridColor } = isDark ? DARK_THEME : LIGHT_THEME;
  return {
    responsive: true,
    maintainAspectRatio: false,
    // Sin animación de entrada: el plugin de iconos (RadarChart) recalcula su posición
    // en cada `afterDraw`; animar el radio de la escala dispararía ese recálculo en
    // cada frame de la animación sin necesidad.
    animation: false as const,
    // Deja margen alrededor del polígono para los iconos que RadarChart superpone
    // fuera de él (ICON_OFFSET + la mitad del icono) — si no, `.ud-radar-chart`
    // (overflow: hidden) los recorta, sobre todo en el eje vertical.
    layout: { padding: 34 },
    plugins: {
      legend: { display: false },
      // El tooltip nativo se dibuja dentro del <canvas>, así que siempre queda detrás
      // de los iconos superpuestos (una capa HTML aparte). Se desactiva aquí y
      // RadarChart lo reemplaza por un overlay HTML propio (ver `external` en
      // RadarChart/index.tsx) que sí puede apilarse por encima con z-index.
      tooltip: { enabled: false },
    },
    scales: {
      r: {
        min: 0,
        max: MAX_SCORE,
        ticks: { stepSize: 1, display: false },
        grid: { color: gridColor, borderDash: DASH_PATTERN },
        border: { dash: DASH_PATTERN },
        angleLines: { color: gridColor, borderDash: DASH_PATTERN },
        // Los iconos (RadarChart) reemplazan el texto; el nombre completo solo aparece
        // en el tooltip nativo al pasar el cursor sobre un punto de dato.
        pointLabels: { display: false },
      },
    },
  };
}
